import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Calendar, MapPin } from 'lucide-react';
import { api } from '@/api';
import { formatDate, formatTime, resolveMediaUrl } from '@/utils/helpers';
import { VideoTheater } from '@/components/common/VideoTheater';
import { SEO } from '@/components/common/SEO';

/**
 * Dedicated, deep-linkable watch page for a single game.
 * Wraps the existing VideoTheater (modal) and opens it immediately.
 * Closing the modal returns the user to the homepage (or previous page if available).
 */
export const WatchPage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.getGame(gameId);
        if (!alive) return;
        setGame(res.data);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [gameId]);

  const closeTheater = () => {
    // Prefer going back if there's history; fall back to home.
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-gray-400 text-sm" data-testid="watch-loading">Loading match...</div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="watch-not-found">
        <h1 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Match not found
        </h1>
        <p className="text-gray-400 mb-6">
          This match may have been removed or the link is incorrect.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-wpsl-red hover:bg-wpsl-blue text-white rounded-full px-6 py-2.5 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>
    );
  }

  const title = `${game.home_team_name || 'TBD'} vs ${game.away_team_name || 'TBD'}`;
  const description = [
    game.league_name,
    formatDate(game.scheduled_time),
    game.production_company_name && `Produced by ${game.production_company_name}`,
  ].filter(Boolean).join(' • ');

  return (
    <div className="min-h-screen" data-testid="watch-page">
      <SEO
        title={title}
        description={description}
        url={`/watch/${game.id}`}
        image={resolveMediaUrl(game.thumbnail)}
      />

      {/* Header strip with match metadata (shown behind the modal) */}
      <div
        className="relative overflow-hidden border-b border-wpsl-blue/30"
        style={{ background: 'linear-gradient(135deg, #001428 0%, #000814 60%, #1a0506 100%)' }}
      >
        <div className="max-w-5xl mx-auto px-4 py-10">
          <button
            onClick={closeTheater}
            className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-4 transition-colors"
            data-testid="watch-back-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-xs uppercase tracking-widest text-wpsl-red font-bold mb-2">
            {game.league_name || 'WPSL Match'}
          </div>
          <h1
            className="text-4xl sm:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-gray-400 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(game.scheduled_time)} • {formatTime(game.scheduled_time)}
            </span>
            {game.production_company_name && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                Produced by{' '}
                <span className="text-wpsl-blue font-semibold ml-1">
                  {game.production_company_name}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Auto-open the theater modal (reuses existing embedded/external logic) */}
      <VideoTheater game={game} isOpen={true} onClose={closeTheater} />
    </div>
  );
};

export default WatchPage;
